/**
 * Backend client for HTTP data transfer and WebSocket progress updates
 * Handles communication with Python FastAPI server
 * 
 * CENTRALIZED: All backend communication should go through this client.
 * Do not hardcode backend URLs elsewhere in the codebase.
 */

import { authService, BackendClientBase } from "@carousel-office/shared";
import {
  PYTHON_BACKEND_URL,
  EXCEL_BACKEND_URL,
} from "@carousel-office/shared";
import {
  normalizeWebSocketEvent,
  type WebSocketUpdateEnvelope,
} from "./websocketEvents";

/**
 * Base URL for the Python backend server (main backend - handles PPT operations).
 * Single source of truth - use this constant or BackendClient methods.
 */
export const BACKEND_BASE_URL = PYTHON_BACKEND_URL;

/**
 * Base URL for the Excel microservice backend (handles Excel/Aspose.Cells operations).
 * Separate server to avoid Aspose.Cells / Aspose.Slides dependency conflicts.
 */
export const EXCEL_BACKEND_BASE_URL = EXCEL_BACKEND_URL;

export interface ProcessingRequest {
  documentId: string;
  slideId: string;
  slideIndex: number;  // 0-based position in deck
  imageBase64: string;
  xmlBase64?: string;
  isUpdate: boolean;
  previousAnalysis?: any;
  timestamp: number;
  metadata?: {
    layoutName?: string;
    layoutId?: string;  // Layout ID from Office.js (if available)
    masterTemplateId?: string;  // Master template ID from Office.js
    masterTemplateName?: string;  // Master template name from Office.js
    slideCount?: number;
  };
}

export interface BulkProcessingRequest {
  documentId: string;
  slides: ProcessingRequest[];
  trigger: 'startup' | 'bulk_add' | 'manual';
}

/**
 * Progress update format (normalized envelope + legacy fields).
 */
export interface ProgressUpdate extends WebSocketUpdateEnvelope<WebSocketUpdateData> {
  // Legacy flat fields (optional).
  documentId?: string;
  slideId?: string;
  current?: number;
  total?: number;
  message?: string;
  percentage?: number;
}

/**
 * Standardized WebSocket update data payload
 */
export interface WebSocketUpdateData {
  task_id?: string;
  status?: string;
  slide_ids?: string[];
  descriptions?: Array<{
    slide_id: string;
    short_description: string;
  }>;
  batch?: number;
  batch_size?: number;
  estimated_batches?: number;
  total_batches?: number;
  slides_in_batch?: number;
  total_slides?: number;
  slides_processed?: number;
  slides_failed?: number;
  batches_processed?: number;
  max_concurrent?: number;
  processing_mode?: string;
  source_file?: string;
  failed_slides?: Array<{
    slide_id: string;
    error: string;
  }>;
  reason?: string;
  filepath?: string;
  current?: number;
  total?: number;
  error?: string;
  stage?: string;
  progress?: number;
  [key: string]: unknown;
}

export interface BackendStatus {
  isAvailable: boolean;
  queueLength: number;
  activeProcessing: number;
  version?: string;
}

export interface PresentationCatalogEntry {
  deck_id: string;
  title: string;
  slide_count: number;
  status: 'opening' | 'deferred' | 'processing' | 'partial' | 'complete' | 'error';
  created_at?: string;
  last_processed?: string;
}

export interface PresentationCatalogUpsertRequest {
  documentId: string;
  deckTitle?: string;
  slideCount?: number;
  status: 'opening' | 'deferred' | 'processing' | 'partial' | 'complete' | 'error';
}

export type SettingsMode = "powerpoint" | "excel";

export interface BackendCustomInstruction {
  id: string;
  title: string;
  instruction: string;
  on: boolean;
}

export interface BackendCustomInstructions {
  carouselChat: BackendCustomInstruction[];
  search: BackendCustomInstruction[];
  research: BackendCustomInstruction[];
  execution: BackendCustomInstruction[];
}

export interface BackendExcelColors {
  isExcelSemanticFormattingEnabled: boolean;
  excelApplyColorsOnlyToEmptyCells: boolean;
  excelHardcodedFontColor: string;
  excelCalculationFontColor: string;
  excelReferenceOtherSheetFontColor: string;
  excelReferenceOtherWorkbookFontColor: string;
  excelReferenceExternalDataFontColor: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// COM Operation Response Types
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Base response for all COM operations
 */
export interface ComOperationResponse {
  success: boolean;
  message?: string;
}

/**
 * Response from alignShapes operation
 */
export interface AlignShapesResponse extends ComOperationResponse {
  aligned_count: number;
}

/**
 * Response from distributeShapes operation
 */
export interface DistributeShapesResponse extends ComOperationResponse {
  distributed_count: number;
}

/**
 * Response from gridAlign operation
 */
export interface GridAlignResponse extends ComOperationResponse {
  positioned_count: number;
  cells_filled: number;
  resized_count?: number;
  scaled_count?: number;
}

/**
 * Response from ringAlign operation
 */
export interface RingAlignResponse extends ComOperationResponse {
  positioned_count: number;
  slots_filled: number;
}

/**
 * Response from resizeShapesToFitArea operation
 */
export interface ResizeShapesToFitAreaResponse extends ComOperationResponse {
  resized_count: number;
  groups_processed: number;
  text_scaled_count?: number;
}

/**
 * Response from reorderShapes operation
 */
export interface ReorderShapesResponse extends ComOperationResponse {
  reordered_count: number;
  positions_swapped: number;
}

/**
 * Response from setZOrder operation
 */
export interface SetZOrderResponse extends ComOperationResponse {
  modified_count: number;
}

/**
 * Response from setPositions operation
 */
export interface SetPositionsResponse extends ComOperationResponse {
  positioned_count: number;
  text_scaled_count?: number;
  failed_shapes?: number[];
}

/**
 * Response from alignToTable operation
 */
export interface AlignToTableResponse extends ComOperationResponse {
  aligned_count?: number;
}

/**
 * Response from groupShapes operation
 */
export interface GroupShapesResponse extends ComOperationResponse {
  group_id?: number;
  data?: { group_id?: number };
}

/**
 * Response from ungroupShapes operation
 */
export interface UngroupShapesResponse extends ComOperationResponse {
  ungrouped_shape_ids?: number[];
  data?: { ungrouped_shape_ids?: number[] };
}

type ProgressHandler = (update: ProgressUpdate) => void;
type ConnectionHandler = (connected: boolean) => void;

class SharedBackendHttpClient extends BackendClientBase {
  public async requestPublic(
    path: string,
    init: RequestInit = {},
    options: { requireAuth?: boolean; retries?: number } = {}
  ): Promise<Response> {
    return this.request(path, init, options);
  }

  public async requestJsonPublic<T>(
    path: string,
    init: RequestInit = {},
    options: { requireAuth?: boolean; retries?: number } = {}
  ): Promise<T> {
    return this.requestJson<T>(path, init, options);
  }
}

export class BackendClient {
  public baseUrl: string;  // Public for external access
  private wsUrl: string;
  private readonly pythonApiClient: SharedBackendHttpClient;
  private readonly excelApiClient: SharedBackendHttpClient;
  private websocket?: WebSocket;
  private progressHandlers: Set<ProgressHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private reconnectTimer?: number;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private reconnectAttempts = 0;
  private isConnected = false;
  private currentdocumentId?: string;  // Store deck ID for reconnections

  constructor(
    baseUrl: string = BACKEND_BASE_URL,
    wsPath: string = '/ws/progress'
  ) {
    this.baseUrl = baseUrl;
    this.wsUrl = baseUrl.replace(/^http/, 'ws') + wsPath;
    this.pythonApiClient = new SharedBackendHttpClient({
      getBaseUrl: () => this.baseUrl,
      getAuthHeader: () => authService.getAuthHeader(),
      serviceName: "BackendClient/python",
    });
    this.excelApiClient = new SharedBackendHttpClient({
      getBaseUrl: () => EXCEL_BACKEND_BASE_URL,
      getAuthHeader: () => authService.getAuthHeader(),
      serviceName: "BackendClient/excel",
    });
  }

  /**
   * Unified method to process any number of slides with intelligent batching.
   * Automatically determines optimal batch size and concurrency based on slide count.
   * 
   * @param request - Unified processing request with slides array
   * @returns Processing response with task_id and status
   */
  async processSlides(request: {
    documentId: string;
    deckTitle?: string;  // Presentation title from Office.js
    slides: Array<{
      slideId: string;
      slideIndex: number;
      imageBase64: string;
      xmlBase64?: string;
      metadata?: {
        layoutName?: string;
        layoutId?: string;
        masterTemplateId?: string;
        masterTemplateName?: string;
        slideCount?: number;
      };
    }>;
    batchSize?: number;  // Optional: override auto-detected batch size
    maxConcurrent?: number;  // Optional: override auto-detected concurrency
    trigger?: 'startup' | 'bulk_add' | 'manual' | 'single';
  }): Promise<any> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }
    
    const numSlides = request.slides.length;
    console.log(`[BackendClient] 📤 Processing ${numSlides} slide${numSlides > 1 ? 's' : ''} via unified endpoint`);
    console.log(`[BackendClient] Deck ID: ${request.documentId}`);
    console.log(`[BackendClient] Trigger: ${request.trigger || 'manual'}`);
    console.log(`[BackendClient] Base URL: ${this.baseUrl}`);
    console.log(`[BackendClient] Endpoint: ${this.baseUrl}/process_slides_live`);
    
    const requestBody = {
      documentId: request.documentId,
      deckTitle: request.deckTitle,
      slides: request.slides,
      batchSize: request.batchSize,
      maxConcurrent: request.maxConcurrent,
      trigger: request.trigger || 'manual'
    };
    
    console.log(`[BackendClient] Request body size: ${JSON.stringify(requestBody).length} bytes`);
    console.log(`[BackendClient] First slide preview:`, {
      slideId: request.slides[0]?.slideId,
      slideIndex: request.slides[0]?.slideIndex,
      imageSize: request.slides[0]?.imageBase64?.length || 0,
      xmlSize: request.slides[0]?.xmlBase64?.length || 0
    });
    
    try {
      const response = await this.pythonApiClient.requestPublic("/process_slides_live", {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Deck-ID': request.documentId
        },
        body: JSON.stringify(requestBody)
      }, { requireAuth: true, retries: 0 });
      
      console.log(`[BackendClient] Response status: ${response.status} ${response.statusText}`);
      const headersObj: Record<string, string> = {};
      response.headers.forEach((value, key) => { headersObj[key] = value; });
      console.log(`[BackendClient] Response headers:`, headersObj);
      
      const result = await response.json();
      console.log(`[BackendClient] ✅ Processing initiated:`, result);
      return result;
    } catch (error) {
      console.error(`[BackendClient] ❌ Fetch error:`, error);
      throw error;
    }
  }

  /**
   * Get backend status
   */
  async getStatus(): Promise<BackendStatus> {
    try {
      const status = await this.pythonApiClient.requestJsonPublic<Record<string, unknown>>("/status", {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }, { retries: 0 });

      return {
        isAvailable: true,
        ...status,
        queueLength: Number(status.queueLength ?? 0),
        activeProcessing: Number(status.activeProcessing ?? 0),
      };
    } catch (error) {
      return {
        isAvailable: false,
        queueLength: 0,
        activeProcessing: 0
      };
    }
  }

  /**
   * Unified search endpoint - the main search method
   * Supports semantic, exact, fuzzy, and visual search types
   */
  async searchUnified(options: {
    documentId?: string;
    searchType: 'semantic' | 'exact' | 'fuzzy' | 'visual';
    resultTypes?: Array<'slide' | 'component' | 'asset' | 'shape' | 'layout'>;
    queries?: string[];
    queryOperator?: 'or' | 'and';
    numResults?: number;
    presentationIds?: string[];
    slidesToInclude?: string[];
    slidesToExclude?: string[];
    componentIdsToExclude?: string[];
    assetIdsToExclude?: string[];
    layoutIdsToExclude?: string[];
    shapeIdsToExclude?: string[];
    enrichResults?: boolean;  // Include full data like image_base64
    visualSource?: {
      slideId?: string;
      slideIndex?: number;
      shapeIds?: string[];
      componentId?: string;
    };
  }): Promise<any> {
    return this.pythonApiClient.requestJsonPublic<any>(
      "/search/unified",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deck_id: options.documentId,
          search_type: options.searchType,
          result_types: options.resultTypes ?? ["slide", "component"],
          queries: options.queries ?? [],
          query_operator: options.queryOperator ?? "or",
          num_results: options.numResults ?? 20,
          presentation_ids: options.presentationIds,
          slides_to_include: options.slidesToInclude,
          slides_to_exclude: options.slidesToExclude,
          component_ids_to_exclude: options.componentIdsToExclude,
          asset_ids_to_exclude: options.assetIdsToExclude,
          layout_ids_to_exclude: options.layoutIdsToExclude,
          shape_ids_to_exclude: options.shapeIdsToExclude,
          enrich_results: options.enrichResults ?? true,
          visual_source: options.visualSource
            ? {
                slide_id: options.visualSource.slideId,
                slide_index: options.visualSource.slideIndex,
                shape_ids: options.visualSource.shapeIds,
                component_id: options.visualSource.componentId,
              }
            : undefined,
        }),
      },
      { requireAuth: true, retries: 0 }
    );
  }

  /**
   * Search slides using semantic search (wrapper around searchUnified)
   */
  async searchSlides(
    documentId: string,
    query: string,
    limit: number = 10
  ): Promise<any> {
    return this.searchUnified({
      documentId,
      searchType: 'semantic',
      queries: [query],
      resultTypes: ['slide'],
      numResults: limit,
    });
  }

  /**
   * Text search - exact or fuzzy (wrapper around searchUnified)
   */
  async searchText(options: {
    query: string;
    mode?: 'exact' | 'fuzzy';
    limit?: number;
    presentationIds?: string[];
    slideIds?: string[];
    categories?: string[];
    shapeIds?: string[];
    masterIds?: string[];
    componentIds?: string[];
    assetIds?: string[];
  }): Promise<any> {
    return this.searchUnified({
      searchType: options.mode === 'fuzzy' ? 'fuzzy' : 'exact',
      queries: [options.query],
      numResults: options.limit ?? 20,
      presentationIds: options.presentationIds,
      slidesToInclude: options.slideIds,
      resultTypes: ['slide', 'component', 'shape'],
    });
  }

  /**
   * Get deck hierarchy
   */
  async getDeckHierarchy(documentId: string): Promise<any> {
    return this.pythonApiClient.requestJsonPublic<any>(
      `/deck_hierarchy/${documentId}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
      { retries: 0 }
    );
  }

  async resetDatabase(): Promise<void> {
    await this.pythonApiClient.requestPublic(
      "/admin/reset",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      },
      { requireAuth: true, retries: 0 }
    );
  }

  /**
   * Connect to WebSocket for progress updates
   */
  connectProgressWebSocket(documentId?: string): void {
    // Store deck ID for reconnections
    if (documentId) {
      this.currentdocumentId = documentId;
    }
    
    if (this.websocket?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }
    
    const url = this.currentdocumentId ? `${this.wsUrl}/${this.currentdocumentId}` : this.wsUrl;
    
    try {
      this.websocket = new WebSocket(url);
      
      this.websocket.onopen = () => {
        console.log('WebSocket connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.notifyConnectionChange(true);
        
        // Send authentication handshake with JWT token
        const token = authService.getAccessToken();
        if (token && this.currentdocumentId) {
          this.websocket!.send(JSON.stringify({
            token: token,
            deck_id: this.currentdocumentId
          }));
          console.log('Sent WebSocket authentication');
        } else {
          console.warn('Missing token or deck_id for WebSocket auth');
        }
      };
      
      this.websocket.onmessage = (event) => {
        try {
          const rawUpdate = JSON.parse(event.data) as Record<string, unknown>;
          const update = normalizeWebSocketEvent(rawUpdate) as ProgressUpdate;
          // DEBUG: Log all WebSocket messages to track RAG progress
          const msgType = update.update_type || update.type || 'unknown';
          if (msgType.includes('rag') || msgType.includes('file')) {
            console.log(`[WS_DEBUG] 📨 RAG message received:`, msgType, update);
          } else {
            console.log(`[WS_DEBUG] 📨 Message type: ${msgType}`);
          }
          this.handleProgressUpdate(update);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };
      
      this.websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      this.websocket.onclose = (event) => {
        console.log(`WebSocket closed: ${event.code} - ${event.reason}`);
        this.isConnected = false;
        this.notifyConnectionChange(false);
        
        // Attempt to reconnect
        if (!event.wasClean) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnectWebSocket(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    
    if (this.websocket) {
      this.websocket.close(1000, 'Client disconnecting');
      this.websocket = undefined;
    }
    
    this.isConnected = false;
    this.notifyConnectionChange(false);
  }

  disconnectProgressWebSocket(): void {
    this.disconnectWebSocket();
  }

  /**
   * Schedule WebSocket reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      return; // Already scheduled
    }
    
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );
    
    console.log(`Scheduling WebSocket reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connectProgressWebSocket();
    }, delay);
  }

  /**
   * Handle incoming progress update
   */
  private handleProgressUpdate(update: ProgressUpdate): void {
    // DEBUG: Log handler count
    const handlerCount = this.progressHandlers.size;
    const msgType = update.update_type || update.type || 'unknown';
    if (msgType.includes('rag')) {
      console.log(`[WS_DEBUG] handleProgressUpdate: ${handlerCount} handlers registered for RAG message`);
    }
    
    // Notify all handlers
    this.progressHandlers.forEach(handler => {
      try {
        handler(update);
      } catch (error) {
        console.error('Error in progress handler:', error);
      }
    });
  }

  /**
   * Register progress update handler
   */
  onProgress(handler: ProgressHandler): void {
    this.progressHandlers.add(handler);
  }

  /**
   * Remove progress update handler
   */
  offProgress(handler: ProgressHandler): void {
    this.progressHandlers.delete(handler);
  }

  /**
   * Register connection change handler
   */
  onConnectionChange(handler: ConnectionHandler): void {
    this.connectionHandlers.add(handler);
  }

  /**
   * Remove connection change handler
   */
  offConnectionChange(handler: ConnectionHandler): void {
    this.connectionHandlers.delete(handler);
  }

  /**
   * Notify connection change
   */
  private notifyConnectionChange(connected: boolean): void {
    this.connectionHandlers.forEach(handler => {
      try {
        handler(connected);
      } catch (error) {
        console.error('Error in connection handler:', error);
      }
    });
  }

  /**
   * Send a message through WebSocket
   */
  sendWebSocketMessage(message: any): void {
    if (this.websocket?.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isWebSocketConnected(): boolean {
    return this.isConnected && this.websocket?.readyState === WebSocket.OPEN;
  }

  /**
   * Check if backend is available
   */
  async isBackendAvailable(): Promise<boolean> {
    try {
      const status = await this.getStatus();
      return status.isAvailable;
    } catch {
      return false;
    }
  }

  /**
   * Process offline presentation via LibreOffice
   */
  async processOfflinePresentation(
    filePath: string,
    documentId: string
  ): Promise<void> {
    await this.pythonApiClient.requestJsonPublic(
      "/process_offline_pptx",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filePath, documentId }),
      },
      { retries: 0 }
    );
  }

  /**
   * Visual search - find visually similar content (wrapper around searchUnified)
   */
  async searchVisual(options: {
    assetId?: string;
    slideId?: string;
    componentId?: string;
    shapeIds?: string[];
    presentationIds?: string[];
    slideIds?: string[];
    assetIds?: string[];
    limit?: number;
  }): Promise<any> {
    // Build visual source from options
    const visualSource: any = {};
    
    if (options.slideId) {
      visualSource.slideId = options.slideId;
      if (options.shapeIds) {
        visualSource.shapeIds = options.shapeIds;
      }
    } else if (options.componentId) {
      visualSource.componentId = options.componentId;
    }
    
    return this.searchUnified({
      searchType: 'visual',
      visualSource: Object.keys(visualSource).length > 0 ? visualSource : undefined,
      resultTypes: ['slide', 'component', 'asset'],
      numResults: options.limit ?? 10,
      presentationIds: options.presentationIds,
      slidesToInclude: options.slideIds,
    });
  }

  /**
   * Mark slides as unavailable (deleted) in the backend.
   * Sets is_available=false in the slide JSON for search exclusion.
   */
  async markSlidesUnavailable(documentId: string, slideIds: string[]): Promise<{
    status: string;
    deck_id: string;
    marked_unavailable: number;
    total_requested: number;
    slide_ids: string[];
    errors?: Array<{ slide_id: string; error: string }>;
  }> {
    console.log(`[BackendClient] Marking ${slideIds.length} slides as unavailable in deck ${documentId}`);

    return this.pythonApiClient.requestJsonPublic<{
      status: string;
      deck_id: string;
      marked_unavailable: number;
      total_requested: number;
      slide_ids: string[];
      errors?: Array<{ slide_id: string; error: string }>;
    }>(
      "/slides/mark_unavailable",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ documentId, slideIds }),
      },
      { requireAuth: true, retries: 0 }
    );
  }

  /**
   * Get short descriptions for multiple slides.
   * Returns shortDescription (under 80 chars) for each requested slide.
   */
  async getSlideShortDescriptions(documentId: string, slideIds: string[]): Promise<{
    deck_id: string;
    slides: Array<{
      slide_id: string;
      shortDescription: string;
      is_available: boolean;
      error?: string;
    }>;
    count: number;
  }> {
    console.log(`[BackendClient] Fetching descriptions for ${slideIds.length} slides in deck ${documentId}`);

    return this.pythonApiClient.requestJsonPublic<{
      deck_id: string;
      slides: Array<{
        slide_id: string;
        shortDescription: string;
        is_available: boolean;
        error?: string;
      }>;
      count: number;
    }>(
      "/slides/short_descriptions",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ documentId, slideIds }),
      },
      { requireAuth: true, retries: 0 }
    );
  }

  /**
   * Update slide indexes when slides are reordered in the presentation.
   * Syncs the backend JSON files with the current slide order.
   */
  async updateSlideIndexes(documentId: string, mappings: Array<{ slideId: string; slideIndex: number }>): Promise<{
    status: string;
    deck_id: string;
    updated_count: number;
    total_requested: number;
    errors?: Array<{ slide_id: string; slide_index: number; error: string }>;
  }> {
    console.log(`[BackendClient] Updating indexes for ${mappings.length} slides in deck ${documentId}`);

    return this.pythonApiClient.requestJsonPublic<{
      status: string;
      deck_id: string;
      updated_count: number;
      total_requested: number;
      errors?: Array<{ slide_id: string; slide_index: number; error: string }>;
    }>(
      "/slides/update_indexes",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ documentId, mappings }),
      },
      { requireAuth: true, retries: 0 }
    );
  }

  // =========================================================================
  // Shape Alignment Operations (Python COM Backend)
  // =========================================================================

  /**
   * Set z-order of shapes using Python COM backend.
   * 
   * Supports two modes:
   * 1. Sequence mode (preferred): Pass zOrderSequence array (shapes from back to front)
   * 2. Legacy mode: Pass shapeIds + position enum
   */
  async setZOrder(
    slideNumber: number, 
    shapeIdsOrSequence: number[], 
    position?: 'front' | 'back' | 'forward' | 'backward',
    options?: { useSequenceMode?: boolean }
  ): Promise<any> {
    const useSequenceMode = options?.useSequenceMode ?? (position === undefined);
    
    if (useSequenceMode) {
      // Sequence mode: shapes ordered from back to front
      console.log(`[BackendClient] Setting z-order sequence for ${shapeIdsOrSequence.length} shapes on slide ${slideNumber}`);
      
      const response = await fetch(`${this.baseUrl}/com/setZOrder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId: 'current',
          slideId: slideNumber,
          z_order_sequence: shapeIdsOrSequence
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Z-order sequence operation failed: ${errorText || response.statusText}`);
      }

      return response.json();
    } else {
      // Legacy mode: position enum
      console.log(`[BackendClient] Setting z-order to '${position}' for ${shapeIdsOrSequence.length} shapes on slide ${slideNumber}`);

      const response = await fetch(`${this.baseUrl}/com/setZOrder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId: 'current',
          slideId: slideNumber,
          shape_ids: shapeIdsOrSequence,
          position
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Z-order operation failed: ${errorText || response.statusText}`);
      }

      return response.json();
    }
  }
  
  /**
   * Set z-order using sequence mode (shapes ordered from back to front)
   */
  async setZOrderSequence(slideNumber: number, zOrderSequence: number[]): Promise<any> {
    return this.setZOrder(slideNumber, zOrderSequence, undefined, { useSequenceMode: true });
  }

  /**
   * Group shapes using Python COM backend
   */
  async groupShapes(slideNumber: number, shapeIds: number[]): Promise<any> {
    console.log(`[BackendClient] Grouping ${shapeIds.length} shapes on slide ${slideNumber}`);

    const response = await fetch(`${this.baseUrl}/com/groupShapes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: 'current',
        slideId: slideNumber,
        shape_ids: shapeIds
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Group operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Ungroup shapes using Python COM backend
   */
  async ungroupShapes(slideNumber: number, shapeIds: number[]): Promise<any> {
    console.log(`[BackendClient] Ungrouping ${shapeIds.length} shapes on slide ${slideNumber}`);

    const response = await fetch(`${this.baseUrl}/com/ungroupShapes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: 'current',
        slideId: slideNumber,
        shape_ids: shapeIds
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Ungroup operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Ungroup ALL groups on a slide.
   * Used before space planning to ensure all individual shapes are visible for analysis.
   */
  async ungroupAllOnSlide(slideNumber: number, recursive: boolean = true): Promise<{
    success: boolean;
    message: string;
    ungrouped_groups: number;
    resulting_shapes: number;
    ungrouped_shape_ids: number[];
  }> {
    console.log(`[BackendClient] Ungrouping all groups on slide ${slideNumber} (recursive: ${recursive})`);

    const response = await fetch(`${this.baseUrl}/com/ungroupAllOnSlide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: 'current',
        slideId: slideNumber,
        recursive
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Ungroup all operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Set positions of shapes using Python COM backend
   */
  async setPositions(options: {
    slideNumber: number;
    positions: Array<{
      shapeId: number;
      left: number;
      top: number;
      width?: number;
      height?: number;
    }>;
    scaleFonts?: boolean;
    fontScaleFactor?: number;  // Uniform scale factor for ALL fonts
  }): Promise<any> {
    console.log(`[BackendClient] Setting positions for ${options.positions.length} shapes on slide ${options.slideNumber}`);

    const requestBody: Record<string, unknown> = {
      documentId: 'current',
      slideId: options.slideNumber,
      positions: options.positions.map(p => ({
        shape_id: p.shapeId,
        left: p.left,
        top: p.top,
        width: p.width,
        height: p.height,
      })),
      scale_fonts: options.scaleFonts ?? true,
    };

    // Only include font_scale_factor if provided
    if (options.fontScaleFactor !== undefined) {
      requestBody.font_scale_factor = options.fontScaleFactor;
    }

    const response = await fetch(`${this.baseUrl}/com/setPositions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Set positions operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Align shapes to a table cell using Python COM backend.
   * 
   * Supports two formats:
   * 1. New format (Notion spec): cellReference object with nested row/column
   * 2. Legacy format: row/column at top level
   */
  async alignToTable(options: {
    slideNumber: number;
    shapeIds: number[];
    tableShapeId: number;
    // New format (preferred) - 1-based indices
    cellReference?: { row: number; column: number };
    // Legacy format (1-based)
    row?: number;
    column?: number;
    cellAnchor: 'top_left' | 'top_center' | 'top_right' | 'middle_left' | 'middle_center' | 'middle_right' | 'bottom_left' | 'bottom_center' | 'bottom_right';
    alignmentPattern: 'in_cell' | 'next_to_cell';
    bufferPercent?: number;  // 0.0-0.5, buffer as percentage of cell dimension
  }): Promise<any> {
    // Get row/column from either format
    const row = options.cellReference?.row ?? options.row;
    const column = options.cellReference?.column ?? options.column;
    
    if (row === undefined || column === undefined) {
      throw new Error('Must provide either cellReference OR (row + column)');
    }
    
    console.log(`[BackendClient] Aligning ${options.shapeIds.length} shapes to table cell (${row}, ${column}) on slide ${options.slideNumber}${options.bufferPercent ? ` with ${options.bufferPercent * 100}% buffer` : ''}`);

    const response = await fetch(`${this.baseUrl}/com/alignToTable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: 'current',
        slideId: options.slideNumber,
        shape_ids: options.shapeIds,
        table_shape_id: options.tableShapeId,
        cell_reference: { row, column },
        cell_anchor: options.cellAnchor,
        alignment_pattern: options.alignmentPattern,
        buffer_percent: options.bufferPercent ?? 0
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Align to table operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  // =========================================================================
  // High-Level Arrange Operations (Python COM Backend)
  // =========================================================================

  /**
   * Align shapes horizontally or vertically to a common edge/center
   */
  async alignShapes(options: {
    slideNumber: number;
    shapeIds: number[];
    alignDirection: 'horizontal' | 'vertical';
    alignAnchorHorizontal?: 'left' | 'center' | 'right';
    alignAnchorVertical?: 'top' | 'middle' | 'bottom';
    anchorShapeId?: number;
  }): Promise<AlignShapesResponse> {
    console.log(`[BackendClient] Aligning ${options.shapeIds.length} shapes ${options.alignDirection}ly on slide ${options.slideNumber}`);

    const response = await fetch(`${this.baseUrl}/com/alignShapes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: 'current',
        slideId: options.slideNumber,
        shape_ids: options.shapeIds,
        align_direction: options.alignDirection,
        align_anchor_horizontal: options.alignAnchorHorizontal,
        align_anchor_vertical: options.alignAnchorVertical,
        anchor_shape_id: options.anchorShapeId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Align shapes operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Distribute shapes evenly within an area
   */
  async distributeShapes(options: {
    slideNumber: number;
    shapeIds: number[];
    distributeDirection: 'horizontal' | 'vertical';
    distributionArea?: { left: number; right: number; top: number; bottom: number };
  }): Promise<DistributeShapesResponse> {
    console.log(`[BackendClient] Distributing ${options.shapeIds.length} shapes ${options.distributeDirection}ly on slide ${options.slideNumber}`);

    const response = await fetch(`${this.baseUrl}/com/distributeShapes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: 'current',
        slideId: options.slideNumber,
        shape_ids: options.shapeIds,
        distribute_direction: options.distributeDirection,
        distribution_area: options.distributionArea
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Distribute shapes operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Arrange shapes into a grid layout
   */
  async gridAlignShapes(options: {
    slideNumber: number;
    shapeGroups: number[][];  // Shapes per cell
    gridDimensions: { rows: number; columns: number };
    gridArea: { left: number; top: number; width: number; height: number };
    columnRatios?: number[];
    rowRatios?: number[];
    cellAnchorHorizontal?: 'left' | 'center' | 'right';
    cellAnchorVertical?: 'top' | 'middle' | 'bottom';
  }): Promise<GridAlignResponse> {
    console.log(`[BackendClient] Grid aligning shapes in ${options.gridDimensions.rows}x${options.gridDimensions.columns} grid on slide ${options.slideNumber}`);

    const response = await fetch(`${this.baseUrl}/com/gridAlign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: 'current',
        slideId: options.slideNumber,
        shape_groups: options.shapeGroups,
        grid_dimensions: options.gridDimensions,
        grid_area: options.gridArea,
        column_ratios: options.columnRatios,
        row_ratios: options.rowRatios,
        cell_anchor_horizontal: options.cellAnchorHorizontal,
        cell_anchor_vertical: options.cellAnchorVertical
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Grid align operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Arrange shapes on an ellipse/ring perimeter
   */
  async ringAlignShapes(options: {
    slideNumber: number;
    shapeGroups: number[][];  // Shapes per slot
    ellipseBounds: { left: number; top: number; width: number; height: number };
    totalSlots: number;
    startAngle?: number;
  }): Promise<RingAlignResponse> {
    console.log(`[BackendClient] Ring aligning shapes in ${options.totalSlots} slots on slide ${options.slideNumber}`);

    const response = await fetch(`${this.baseUrl}/com/ringAlign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: 'current',
        slideId: options.slideNumber,
        shape_groups: options.shapeGroups,
        ellipse_bounds: options.ellipseBounds,
        total_slots: options.totalSlots,
        start_angle: options.startAngle ?? 0
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Ring align operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Resize shape groups to fit within a target area
   */
  async resizeShapesToFitArea(options: {
    slideNumber: number;
    shapes: number[];  // Single group scaled as a unit
    resizeArea: { left: number; top: number; width: number; height: number };
    scaleFonts?: boolean;
    scaleMode?: "uniform" | "fitWidth" | "fitHeight";
  }): Promise<ResizeShapesToFitAreaResponse> {
    console.log(`[BackendClient] Resizing ${options.shapes.length} shapes on slide ${options.slideNumber}`);

    const response = await fetch(`${this.baseUrl}/com/resizeShapesToFitArea`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: 'current',
        slideId: options.slideNumber,
        shapes: options.shapes,
        resize_area: options.resizeArea,
        scale_fonts: options.scaleFonts ?? true,
        scale_mode: options.scaleMode ?? "uniform",
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Resize shapes operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Reorder shapes by swapping their positions
   */
  async reorderShapes(options: {
    slideNumber: number;
    originalSequence: number[][];  // Current reading order
    newSequence: number[][];       // Desired reading order
  }): Promise<ReorderShapesResponse> {
    console.log(`[BackendClient] Reordering shapes on slide ${options.slideNumber}`);

    const response = await fetch(`${this.baseUrl}/com/reorderShapes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId: 'current',
        slideId: options.slideNumber,
        original_sequence: options.originalSequence,
        new_sequence: options.newSequence
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Reorder shapes operation failed: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  // =========================================================================
  // Image Search API Methods
  // =========================================================================

  /**
   * Search for images in a presentation using semantic search.
   * Searches through vectorized image descriptions to find matching images.
   * 
   * @param documentId - The deck ID to search within
   * @param query - Semantic search query (e.g., "company logo", "executive headshot")
   * @param limit - Maximum number of results
   * @returns Array of image results with slide/shape IDs and similarity scores
   */
  async searchImages(documentId: string, query: string, limit: number = 10): Promise<{
    success: boolean;
    results: Array<{
      slide_id: string;
      shape_id: string;
      description: string;
      similarity: number;
      shape_type: string;
      has_image: boolean;
      image_base64?: string;  // Included from unified search when available
    }>;
    total_found: number;
    error?: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Searching images in deck ${documentId}: "${query}"`);

    try {
      // Use unified search with asset result type - images are indexed as assets, not shapes
      // The search_indexer extracts picture/image shapes into the 'assets' category
      // enrichResults: false - image data is retrieved via /context/resolve when needed
      const searchResult = await this.searchUnified({
        documentId,
        searchType: 'semantic',
        queries: [query],
        resultTypes: ['asset'],
        numResults: limit * 2, // Request more to allow filtering
        enrichResults: false,  // Image data retrieved via /context/resolve
      });

      // Filter for image assets (asset_kind === 'image' in backend metadata)
      const imageResults = (searchResult.results || [])
        .filter((r: any) => {
          const assetKind = r.metadata?.asset_kind || r.asset_kind;
          const shapeType = r.shape_type || r.metadata?.shape_type || '';
          // Match image assets by kind or shape type
          return assetKind === 'image' || 
                 shapeType === 'picture' || 
                 shapeType === 'image' ||
                 r.has_image === true;
        })
        .slice(0, limit)
        .map((r: any) => ({
          slide_id: r.slide_id || r.slideId,
          shape_id: r.shape_id || r.shapeId || r.metadata?.shape_id || r.result_id,
          description: r.description || r.text || r.page_content || '',
          similarity: r.score || r.similarity || 0,
          shape_type: r.shape_type || r.metadata?.shape_type || 'picture',
          has_image: true,
          image_base64: r.image_base64,  // Pass through from unified search
        }));

      return {
        success: true,
        results: imageResults,
        total_found: imageResults.length,
      };
    } catch (error) {
      console.error('[BackendClient] Image search error:', error);
      return {
        success: false,
        results: [],
        total_found: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Save an image asset to the local filesystem via the backend.
   * Used during submitResults to persist retrieved images for debugging/inspection.
   */
  async saveAssetToFile(assetId: string, base64: string, mimeType: string): Promise<{
    success: boolean;
    filePath?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/debug/assets/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          asset_id: assetId,
          base64,
          mime_type: mimeType,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `Failed to save asset: ${errorText || response.statusText}`,
        };
      }

      return response.json();
    } catch (error) {
      console.error('[BackendClient] Save asset error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // =========================================================================
  // COM API Methods (PowerPoint automation via Python)
  // =========================================================================

  /**
   * Create a shape via COM backend.
   * Used for operations that Office.js doesn't support (e.g., image fills).
   */
  async comCreateShape(payload: {
    documentId: string;
    operationType: string;
    slideId: number;
    shapeType: string;
    shapeId: number;
    position?: { left: number; top: number; width: number; height: number };
    rotation?: number;
    text?: string;
    textFormatting?: {
      alignment?: string;
    };
    textFormat?: Array<{
      text: string;
      fontFamily?: string;
      fontSize?: number;
      bold?: boolean;
      italic?: boolean;
      underline?: boolean;
      strikethrough?: boolean;
      textColor?: string;
      highlight?: string;
      superscript?: boolean;
      subscript?: boolean;
    }>;
    fillColor?: string;
    fillNull?: boolean;
    fillTransparency?: number;
    outlineColor?: string;
    outlineNull?: boolean;
    imageBase64?: string;
    imageMimeType?: string;
    imageFillMode?: string;
  }): Promise<{
    success: boolean;
    message: string;
    shapeId?: number;
    position?: { left: number; top: number; width: number; height: number };
  }> {
    console.log(`[BackendClient] COM createShape: slide ${payload.slideId}, type ${payload.shapeType}`);

    const response = await fetch(`${this.baseUrl}/com/createShape`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`COM backend error (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  /**
   * Create or edit a connector (freeform path) via COM backend.
   * Uses BuildFreeform for unlimited multi-point paths.
   */
  async comCreateOrEditConnector(payload: {
    documentId: string;
    slideId: number;
    operationType: 'create' | 'edit';
    shapeId?: number;
    connectorConfig: {
      type: 'straight' | 'elbow' | 'curved';
      points: Array<{ x: number; y: number }>;
      lineStyle?: {
        color?: string;
        weight?: number;
        dashStyle?: 'solid' | 'dash' | 'dot' | 'dashDot' | 'longDash';
      };
      arrowheadStart?: {
        style?: 'none' | 'triangle' | 'oval' | 'stealth' | 'diamond' | 'open';
        length?: 'short' | 'medium' | 'long';
        width?: 'narrow' | 'medium' | 'wide';
      };
      arrowheadEnd?: {
        style?: 'none' | 'triangle' | 'oval' | 'stealth' | 'diamond' | 'open';
        length?: 'short' | 'medium' | 'long';
        width?: 'narrow' | 'medium' | 'wide';
      };
    };
  }): Promise<{
    success: boolean;
    message: string;
    shapeId?: number;
    connectorType?: string;
    position?: { left: number; top: number; width: number; height: number };
    warnings?: string[];
    errors?: string[];
  }> {
    console.log(`[BackendClient] COM createOrEditConnector: slide ${payload.slideId}, ${payload.operationType}, type ${payload.connectorConfig.type}`);

    const response = await fetch(`${this.baseUrl}/com/createOrEditConnector`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        message: `COM backend error (${response.status}): ${errorText}`,
        errors: [errorText],
      };
    }

    return response.json();
  }

  /**
   * Build a complete slide section via COM backend.
   * Creates multiple shapes, tables, charts, and connectors in a target area.
   */
  async comBuildSection(payload: {
    documentId: string;
    sectionId: string;
    slideId: number;
    postChartMode?: boolean;
    targetArea: {
      left: number;
      top: number;
      width: number;
      height: number;
    };
    collections: Array<{
      type: 'copy' | 'createShape' | 'createTable' | 'createChart' | 'createConnector';
      purpose?: string;
      position?: {
        left?: number;
        top?: number;
        width?: number;
        height?: number;
      };
      rotation?: number;
      shapeType?: string;
      copySource?: {
        componentId?: string;
        slideId?: number;
        shapes?: Array<{
          shapeId: number;
          targetPosition?: {
            left?: number;
            top?: number;
            width?: number;
            height?: number;
          };
        }>;
      };
      tableConfig?: {
        rows: number;
        columns: number;
        columnWidths?: number[];
        rowHeights?: number[];
        cellText?: Array<{
          cell: { row: number; col: number };
          text?: string;
        }>;
      };
      fill?: {
        type: string;
        color?: string;
        transparency?: number;
        null?: boolean;
      };
      textContent?: {
        paragraphs?: Array<{
          setText?: string;
          textFormat?: Array<{
            text: string;
            fontFamily?: string;
            fontSize?: number;
            bold?: boolean;
            italic?: boolean;
            underline?: boolean;
            strikethrough?: boolean;
            textColor?: string;
            highlight?: string;
            superscript?: boolean;
            subscript?: boolean;
          }>;
        }>;
      };
    }>;
  }): Promise<{
    success: boolean;
    message: string;
    sectionId: string;
    slideId: number;
    createdShapes: Array<{
      type: string;
      shapeId: number;
      position: { left: number; top: number; width: number; height: number };
    }>;
    warnings: string[];
    errors: string[];
  }> {
    console.log(`[BackendClient] COM buildSection: section ${payload.sectionId} on slide ${payload.slideId}`);

    const response = await fetch(`${this.baseUrl}/com/buildSection`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`COM backend error (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  // =========================================================================
  // AlphaSense API Methods
  // =========================================================================

  /**
   * AlphaSense batch search - executes multiple queries in parallel on backend
   */
  async alphaSenseSearchBatch(queries: Array<{ query: string; use_findata_agent?: boolean }>): Promise<any> {
    console.log(`[BackendClient] AlphaSense batch search: ${queries.length} queries`);

    const response = await fetch(`${this.baseUrl}/alphasense/search/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ queries }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`AlphaSense API error (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  // =========================================================================
  // Context Resolution Methods
  // =========================================================================

  /**
   * Resolve a context ID to full content including imageBase64.
   * Uses the /context/resolve endpoint.
   * 
   * Context IDs can be:
   * - Composite for shapes: "deck_abc|slide_257|shape_42"
   * 
   * @returns Resolved context with imageBase64 for picture shapes
   */
  async resolveContext(contextId: string): Promise<{
    success: boolean;
    contextId: string;
    resultType: string;
    presentationId: string;
    slideId?: string;
    slideIndex?: number;
    shapeId?: string;
    shapeType?: string;
    description?: string;
    text?: string;
    imageBase64?: string;
    isResolved: boolean;
    error?: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Resolving context: ${contextId}`);

    try {
      const response = await fetch(`${this.baseUrl}/context/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader,
        },
        body: JSON.stringify({ contextId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          contextId,
          resultType: 'unknown',
          presentationId: '',
          isResolved: false,
          error: `Failed to resolve context: ${errorText || response.statusText}`,
        };
      }

      const data = await response.json();
      return {
        success: data.isResolved,
        ...data,
      };
    } catch (error) {
      console.error('[BackendClient] Context resolve error:', error);
      return {
        success: false,
        contextId,
        resultType: 'unknown',
        presentationId: '',
        isResolved: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // =========================================================================
  // Settings Sync Methods
  // =========================================================================

  // =========================================================================
  // COM Shape Operations
  // =========================================================================

  /**
   * Delete shapes via COM backend
   * Used when Office.js deletion fails or isn't available
   */
  async deleteShapesCOM(params: {
    documentId: string;
    slideId: number;
    shapeIds: number[];
  }): Promise<{
    success: boolean;
    message: string;
    deletedShapeIds: number[];
    failedShapeIds: number[];
    warnings: string[];
    errors: string[];
  }> {
    console.log(`[BackendClient] Deleting ${params.shapeIds.length} shapes via COM`);

    const response = await fetch(`${this.baseUrl}/com/deleteShapes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`COM deleteShapes error (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  /**
   * Copy shapes via COM backend
   * Office.js does NOT support shape copying - this is the only method
   */
  async copyShapesCOM(params: {
    documentId: string;
    targetSlideId: number;
    sourceComponentId?: string;
    sourceSlideId?: number;
    shapes: Array<{
      shapeId: number;
      targetPosition?: {
        left?: number;
        top?: number;
        width?: number;
        height?: number;
      };
      textReplacements?: Array<{
        find: string;
        replace: string;
      }>;
    }>;
    targetArea?: {
      left: number;
      top: number;
      width: number;
      height: number;
    };
  }): Promise<{
    success: boolean;
    message: string;
    copiedShapes: Array<{
      sourceShapeId: number;
      newShapeId: number;
      position: {
        left: number;
        top: number;
        width: number;
        height: number;
      };
    }>;
    warnings: string[];
    errors: string[];
  }> {
    console.log(`[BackendClient] Copying ${params.shapes.length} shapes via COM`);

    const response = await fetch(`${this.baseUrl}/com/copyShapes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`COM copyShapes error (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  /**
   * Insert Excel artifact via COM clipboard bridge.
   */
  async insertExcelArtifactViaClipboard(params: {
    documentId: string;
    slideId: number;
    excel_file_path?: string;
    sheet_name?: string;
    artifact_type?: "chart" | "range";
    chart_name?: string;
    range_ref?: string;
    position?: {
      left: number;
      top: number;
      width: number;
      height: number;
    };
    artifact_id?: string;
    link?: boolean;
    insertion_mode?: "image" | "ole";
  }): Promise<{
    success: boolean;
    shape_id?: number;
    message?: string;
    error?: string;
  }> {
    const response = await fetch(`${this.baseUrl}/com/insertExcelArtifactViaClipboard`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`COM insertExcelArtifactViaClipboard error (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  // =========================================================================
  // Settings Sync Methods
  // =========================================================================

  /**
   * Sync settings to Python backend
   * Used by SettingsPersistence for TypeScript-as-leader pattern
   */
  async syncSettings(settings: object): Promise<void> {
    console.log('[BackendClient] Syncing settings to Python backend');

    try {
      const response = await fetch(`${this.baseUrl}/api/settings/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        console.warn('[BackendClient] Settings sync failed:', response.statusText);
      } else {
        console.log('[BackendClient] Settings synced to Python backend');
      }
    } catch (error) {
      // Python backend might not be running, that's okay
      console.debug('[BackendClient] Settings sync unavailable:', error);
    }
  }

  async getCustomInstructions(mode: SettingsMode): Promise<BackendCustomInstructions> {
    const response = await this.pythonApiClient.requestJsonPublic<{
      success?: boolean;
      customInstructions?: BackendCustomInstructions;
    }>(`/api/settings/custom-instructions?mode=${mode}`, {
      method: "GET",
    });

    return response.customInstructions || {
      carouselChat: [],
      search: [],
      research: [],
      execution: [],
    };
  }

  async updateCustomInstructions(
    mode: SettingsMode,
    customInstructions: BackendCustomInstructions
  ): Promise<BackendCustomInstructions> {
    const response = await this.pythonApiClient.requestJsonPublic<{
      success?: boolean;
      customInstructions?: BackendCustomInstructions;
    }>(`/api/settings/custom-instructions?mode=${mode}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(customInstructions),
    });

    return response.customInstructions || customInstructions;
  }

  async getExcelColors(): Promise<BackendExcelColors> {
    const response = await this.pythonApiClient.requestJsonPublic<{
      success?: boolean;
      excelColors?: BackendExcelColors;
    }>("/api/settings/excel-colors", {
      method: "GET",
    });

    return response.excelColors || {
      isExcelSemanticFormattingEnabled: true,
      excelApplyColorsOnlyToEmptyCells: true,
      excelHardcodedFontColor: "#0000FF",
      excelCalculationFontColor: "#000000",
      excelReferenceOtherSheetFontColor: "#1E914E",
      excelReferenceOtherWorkbookFontColor: "#8B0000",
      excelReferenceExternalDataFontColor: "#E30000",
    };
  }

  async updateExcelColors(colors: BackendExcelColors): Promise<BackendExcelColors> {
    const response = await this.pythonApiClient.requestJsonPublic<{
      success?: boolean;
      excelColors?: BackendExcelColors;
    }>("/api/settings/excel-colors", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(colors),
    });

    return response.excelColors || colors;
  }

  // =========================================================================
  // Plans API Methods
  // =========================================================================

  /**
   * Create a new empty plan shell.
   * Called when user clicks "New Plan" in job manager.
   */
  async createPlan(): Promise<{
    plan_id: string;
    output_deck_id: string;
    status: string;
    created_at: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log('[BackendClient] Creating new plan');

    const response = await fetch(`${this.baseUrl}/plans`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create plan: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * List all plans with their current state, versions, and progress.
   * Used to populate the job manager UI.
   */
  async listPlans(status?: string): Promise<{
    plans: Array<{
      plan_id: string;
      output_deck_id: string;
      style_guide_deck_id?: string | null;
      style_guide_presentation_path?: string | null;
      output_presentation_path?: string | null;
      target_slides?: number | null;
      final_slides?: number | null;
      plan_versions: Array<{
        version_id: number;
        plan_json: Record<string, unknown>;
        timestamp: string;
      }>;
      status: string;
      created_at: string;
      updated_at: string;
      progress?: {
        slides_built: number;
        total_slides: number;
        percent_complete: number;
      } | null;
    }>;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    const url = new URL(`${this.baseUrl}/plans`);
    if (status) {
      url.searchParams.append('status', status);
    }

    console.log(`[BackendClient] Listing plans${status ? ` with status: ${status}` : ''}`);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to list plans: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get a single plan with full details including all versions and progress.
   */
  async getPlan(planId: string): Promise<{
    plan_id: string;
    output_deck_id: string;
    style_guide_deck_id?: string | null;
    style_guide_presentation_path?: string | null;
    output_presentation_path?: string | null;
    target_slides?: number | null;
    final_slides?: number | null;
    status: string;
    created_at: string;
    updated_at: string;
    plan_versions: Array<{
      version_id: number;
      plan_json: Record<string, unknown>;
      timestamp: string;
    }>;
    progress?: {
      slides_built: number;
      total_slides: number;
      percent_complete: number;
    } | null;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Getting plan: ${planId}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get plan: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Add a new version to the plan's version history, or revert to a previous version.
   * Called when planner agent produces/modifies plan or user makes edits.
   * 
   * If revertToVersion is set, truncates all versions after that version instead of adding new.
   */
  async updatePlan(
    planId: string,
    planJson: Record<string, unknown>,
    targetSlides?: number,
    label?: string,
    title?: string,
    revertToVersion?: number
  ): Promise<{
    plan_id: string;
    version_id: number;
    timestamp: string;
    label?: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Updating plan: ${planId}`, { label, title, revertToVersion });

    const response = await fetch(`${this.baseUrl}/plans/${planId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        plan_json: planJson,
        target_slides: targetSlides,
        label,
        title,
        revert_to_version: revertToVersion,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update plan: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Permanently delete a plan and all associated data.
   */
  async deletePlan(planId: string): Promise<{ success: boolean }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Deleting plan: ${planId}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete plan: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Upload a static style-guide PPTX for a specific plan.
   * Stores the file server-side and returns a plan-scoped path.
   */
  async uploadStyleGuide(
    planId: string,
    file: File
  ): Promise<{
    plan_id: string;
    filename: string;
    file_size: number;
    style_guide_presentation_path: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    const formData = new FormData();
    formData.append("file", file, file.name);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/style-guide/upload`, {
      method: 'POST',
      headers: {
        'Authorization': authHeader,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to upload style guide: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Set the style guide on the plan and extract all text content.
   * 
   * Two modes based on source:
   * - Static mode (isLive: false): Style guide comes from a file path
   * - Live mode (isLive: true): Style guide comes from TypeScript deck registry
   */
  async parseStyleGuide(
    planId: string,
    options: {
      isLive: boolean;
      styleGuidePresentationPath?: string;
      styleGuidedocumentId?: string;
      styleGuidePresentationName?: string;  // Display name for the style guide
      slidesBase64?: string[];
    }
  ): Promise<{
    plan_id: string;
    style_guide_deck_id: string;
    style_guide_presentation_name?: string | null;  // Display name for the style guide
    parsed_text: {
      slides: Array<{
        slide_index: number;
        layout_id?: string | null;
        layout_name?: string | null;
        shapes: Array<{
          shape_id: string;
          shape_type: string;
          position: { top: number; left: number; width: number; height: number };
          text?: string | null;
          chart_info?: Record<string, unknown> | null;
          table_info?: Record<string, unknown> | null;
          placeholder_type?: string | null;
        }>;
      }>;
      layouts_used: Array<{ layout_id: string; layout_name?: string | null; count: number }>;
      sections: Array<{ name: string; start_index: number; end_index: number }>;
    };
    slide_count: number;
    sections_found: number;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    const requestBody = {
      is_live: options.isLive,
      style_guide_presentation_path: options.styleGuidePresentationPath,
      style_guide_deck_id: options.styleGuidedocumentId,
      style_guide_presentation_name: options.styleGuidePresentationName,
      slides_base64: options.slidesBase64,
    };
    
    console.log(`[BackendClient] Parsing style guide for plan: ${planId}`, {
      isLive: options.isLive,
      styleGuidedocumentId: options.styleGuidedocumentId,
      styleGuidePresentationPath: options.styleGuidePresentationPath,
      styleGuidePresentationName: options.styleGuidePresentationName,
      slidesBase64Count: options.slidesBase64?.length || 0,
      requestBodyKeys: Object.keys(requestBody).filter(k => (requestBody as any)[k] !== undefined),
    });

    const response = await fetch(`${this.baseUrl}/plans/${planId}/parse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to parse style guide: ${errorText || response.statusText}`);
    }

    const result = await response.json();
    
    // Log the full response for debugging
    console.log(`[BackendClient] Parse style guide response:`, {
      planId: result.plan_id,
      styleGuidedocumentId: result.style_guide_deck_id,
      slideCount: result.slide_count,
      sectionsFound: result.sections_found,
      parsedTextSlides: result.parsed_text?.slides?.length || 0,
      parsedTextLayouts: result.parsed_text?.layouts_used?.length || 0,
      parsedTextSections: result.parsed_text?.sections?.length || 0,
    });
    
    // Log detailed slide info
    if (result.parsed_text?.slides) {
      result.parsed_text.slides.forEach((slide: any, index: number) => {
        const textShapes = slide.shapes?.filter((s: any) => s.text && s.text.trim()) || [];
        console.log(`[BackendClient] Slide ${index + 1}:`, {
          slideIndex: slide.slide_index,
          layoutId: slide.layout_id,
          layoutName: slide.layout_name,
          totalShapes: slide.shapes?.length || 0,
          textShapes: textShapes.length,
          textPreview: textShapes.slice(0, 2).map((s: any) => ({
            type: s.shape_type,
            text: (s.text || '').substring(0, 50) + ((s.text?.length || 0) > 50 ? '...' : ''),
          })),
        });
      });
    }
    
    return result;
  }

  /**
   * Start building the presentation.
   * Only one plan can be building at a time.
   */
  async startBuild(
    planId: string,
    outputPresentationPath: string
  ): Promise<{
    plan_id: string;
    status: string;
    progress?: { slides_built: number; total_slides: number; percent_complete: number } | null;
    build_started_at?: string | null;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Starting build for plan: ${planId}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        output_presentation_path: outputPresentationPath,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to start build: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Pause the current build. Can be resumed later.
   */
  async pauseBuild(planId: string): Promise<{
    plan_id: string;
    status: string;
    progress?: { slides_built: number; total_slides: number; percent_complete: number } | null;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Pausing build for plan: ${planId}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/pause`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to pause build: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Resume a paused build from where it left off.
   */
  async resumeBuild(planId: string): Promise<{
    plan_id: string;
    status: string;
    progress?: { slides_built: number; total_slides: number; percent_complete: number } | null;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Resuming build for plan: ${planId}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/resume`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to resume build: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  // =========================================================================
  // Multi-Slide Builder API Methods
  // =========================================================================

  /**
   * Initialize a build - creates working files and prepares build state.
   * 
   * Two modes:
   * - File mode: style_guide_path points to existing .pptx on disk
   * - Live mode: slides_base64 contains pptx base64 from IndexedDB
   */
  async initBuild(
    planId: string,
    params: {
      totalSections: number;
      totalSlides: number;
      styleGuidePath?: string;  // File path (if not open in PowerPoint)
      slidesBase64?: string[];  // List of pptx base64 (if open in PowerPoint)
      styleGuidedocumentId?: string;  // Deck ID for search indexing
      styleGuideName?: string;  // Display name
    }
  ): Promise<{
    success: boolean;
    plan_id: string;
    working_presentation_path: string;
    working_workbook_path: string;
    style_guide_slides_marked_hidden: number;
    style_guide_deck_id: string;
    build_state: {
      plan_id: string;
      job_id: string;
      status: string;
      working_presentation_path?: string;
      working_workbook_path?: string;
      current_section_index: number;
      total_sections: number;
    };
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Initializing build for plan: ${planId}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        total_sections: params.totalSections,
        total_slides: params.totalSlides,
        style_guide_path: params.styleGuidePath,
        slides_base64: params.slidesBase64,
        style_guide_deck_id: params.styleGuidedocumentId,
        style_guide_name: params.styleGuideName,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to initialize build: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Execute a buildArea operation using Aspose.
   * This is a pure execution endpoint - no LLM calls.
   * 
   * @param planId - The plan ID
   * @param slideIndex - Which slide to build on (0-based)
   * @param areaSpec - The buildArea schema (sectionId, targetArea, collections)
   */
  async buildArea(
    planId: string,
    slideIndex: number,
    areaSpec: Record<string, unknown>
  ): Promise<{
    success: boolean;
    checkpoint_id: string;  // 5-digit ID for rollback
    screenshot_base64?: string;  // For TS agent to evaluate
    shapes_created: Array<{ shape_id: number; shape_type: string }>;
    shape_count: number;
    errors: string[];
    warnings: string[];
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Building area on slide ${slideIndex} for plan: ${planId}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/area`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        slide_index: slideIndex,
        area_spec: areaSpec,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to build area: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create a section-level checkpoint (before or after a section).
   * Used for rollback in case of errors.
   */
  async createBuildCheckpoint(
    planId: string,
    params: {
      checkpointType: 'section_before' | 'section_after';
      sectionIndex: number;
    }
  ): Promise<{
    success: boolean;
    checkpoint_id: string;
    checkpoint_path: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Creating ${params.checkpointType} checkpoint at section ${params.sectionIndex}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/checkpoint`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        checkpoint_type: params.checkpointType,
        section_index: params.sectionIndex,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create checkpoint: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Rollback to a previous checkpoint.
   * 
   * @param planId - The plan ID
   * @param checkpointId - Either a 5-digit area ID or section_X_before/after
   */
  async rollbackBuild(
    planId: string,
    checkpointId: string
  ): Promise<{
    success: boolean;
    rolled_back_to: string;
    message: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Rolling back to checkpoint: ${checkpointId}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/rollback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        checkpoint_id: checkpointId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to rollback: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get the current build state for a plan.
   */
  async getBuildState(planId: string): Promise<{
    plan_id: string;
    build_state?: {
      plan_id: string;
      job_id: string;
      status: string;
      working_presentation_path?: string;
      working_workbook_path?: string;
      current_section_index: number;
      total_sections: number;
      completed_sections: string[];
      errors: Array<{
        section_id: string;
        section_name: string;
        error_type: string;
        message: string;
        timestamp: string;
      }>;
    };
    exists: boolean;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Getting build state for plan: ${planId}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/state`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get build state: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Cancel an active build. This stops the build and marks it as cancelled.
   * Cannot be resumed after cancellation.
   */
  async cancelBuild(planId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Cancelling build for plan: ${planId}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to cancel build: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Insert a slide by copying a source slide and clearing content.
   * 
   * This method:
   * - Copies the source slide (preserves layout, master, styling)
   * - Clears all non-placeholder shapes (user content)
   * - Clears text from content placeholders (title, body, etc.)
   * - Keeps auto-updating placeholders (page number, date, footer)
   * - Reorders to target position
   * 
   * @param planId - The plan ID
   * @param params.sourceSlideIndex - 0-based index of the style guide slide to copy
   * @param params.targetPosition - 0-based target position for the new slide
   */
  async insertSlide(
    planId: string,
    params: {
      sourceSlideIndex: number;
      targetPosition: number;
    }
  ): Promise<{
    success: boolean;
    actual_index: number;
    source_slide_index: number;
    shapes_cleared: number;
    placeholders_cleared: number;
    message: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Inserting slide from source ${params.sourceSlideIndex} at position ${params.targetPosition}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/insert-slide`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        source_slide_index: params.sourceSlideIndex,
        target_position: params.targetPosition,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to insert slide: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Insert a slide using pre-processed layout-only PPTX.
   *
   * This is the PREFERRED method for slide insertion:
   * - Uses layout-only PPTX from the database (already has content cleared)
   * - No runtime clearing needed (content was cleared by ShapeSeparator during processing)
   * - More reliable than insertSlide (which tries to clear content at runtime)
   *
   * @param planId - The plan ID
   * @param params.documentId - Carousel deck ID (for looking up layout in DB)
   * @param params.layoutId - Layout ID from presentationLibrarySearchAgent
   * @param params.targetPosition - 0-based target position for the new slide
   */
  async insertSlideFromLayout(
    planId: string,
    params: {
      documentId: string;
      layoutId: string;
      targetPosition: number;
    }
  ): Promise<{
    success: boolean;
    actual_index: number;
    layout_id: string;
    message: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Inserting slide from layout ${params.layoutId} (deck ${params.documentId}) at position ${params.targetPosition}`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/insert-slide-from-layout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        deck_id: params.documentId,
        layout_id: params.layoutId,
        target_position: params.targetPosition,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to insert slide from layout: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Read a slide from the working presentation.
   * Returns parsed slide structure and optional screenshot.
   * 
   * @param planId - The plan ID
   * @param slideIndex - Slide index (0-based)
   */
  async readWorkingSlide(
    planId: string,
    slideIndex: number
  ): Promise<{
    success: boolean;
    slide_index: number;
    slide_data?: {
      shapes: Array<{
        shape_id: string;
        shape_type: string;
        position: { top: number; left: number; width: number; height: number };
        text?: string;
        chart_info?: Record<string, unknown>;
        table_info?: Record<string, unknown>;
      }>;
      layout_name?: string;
      is_hidden: boolean;
      custom_tags?: Record<string, string>;
    };
    screenshot_base64?: string;
    error?: string;
  }> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    console.log(`[BackendClient] Reading slide ${slideIndex} from working presentation`);

    const response = await fetch(`${this.baseUrl}/plans/${planId}/build/read-slide/${slideIndex}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to read slide: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  // =========================================================================
  // Slide Operations (Add Slides Agent)
  // =========================================================================

  /**
   * Add an empty slide with specified layout via COM backend.
   * Used when Office.js slide addition fails or for more control.
   * 
   * Layout Selection Priority:
   * 1. If sourceSlideRef is provided, use layout from that existing slide
   * 2. If layoutName is provided, search CustomLayouts by name (most reliable)
   * 3. Parse layoutId format 'layout_{id}#{index}' and use the #{index}
   * 4. Fall back to first CustomLayout or ppLayoutBlank
   * 
   * NOTE: sourceSlideRef and layoutId/layoutName are mutually exclusive approaches.
   * If sourceSlideRef is provided, layoutId/layoutName are ignored.
   */
  async comAddEmptySlide(params: {
    documentId: string;
    position: number;
    layoutId?: string;  // Layout ID from style guide. Use this OR sourceSlideRef, not both.
    layoutName?: string;  // Optional: layout name for name-based matching (only with layoutId approach)
    sourceSlideRef?: string;  // Copy layout from existing slide (1-based position or 'slide_N'). Use this OR layoutId.
  }): Promise<{
    success: boolean;
    slideId?: string;
    slideIndex?: number;
    layoutUsed?: string;  // Name of the layout actually used
    error?: string;
  }> {
    const layoutInfo = params.layoutId ? `layout ${params.layoutId}${params.layoutName ? `, name: ${params.layoutName}` : ''}` : '';
    const sourceInfo = params.sourceSlideRef ? `sourceSlide: ${params.sourceSlideRef}` : '';
    console.log(`[BackendClient] COM addEmptySlide: position ${params.position}, ${layoutInfo || sourceInfo}`);

    const response = await fetch(`${this.baseUrl}/com/addEmptySlide`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: `COM backend error (${response.status}): ${errorText}`,
      };
    }

    return response.json();
  }

  /**
   * Copy a slide via COM backend (exact duplicate).
   * Office.js doesn't support slide copying - this is the only method.
   */
  async comCopySlide(params: {
    documentId: string;
    position: number;
    sourceSlideId: string;
    clearOptions?: {
      clearTextContent?: boolean;
      resetPlaceholders?: boolean;
      clearTableContent?: boolean;
      clearChartData?: boolean;
      removeNonPlaceholderShapes?: boolean;
    };
  }): Promise<{
    success: boolean;
    slideId?: string;
    slideIndex?: number;
    error?: string;
  }> {
    console.log(`[BackendClient] COM copySlide: from ${params.sourceSlideId} to position ${params.position}`);

    const response = await fetch(`${this.baseUrl}/com/copySlide`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: `COM backend error (${response.status}): ${errorText}`,
      };
    }

    return response.json();
  }

  /**
   * Replace text in shapes via COM backend using PowerPoint's native Replace method.
   * PRESERVES ALL FORMATTING (fonts, colors, bullets, etc.) - essential for clone+edit workflows.
   * Office.js setText overwrites formatting, so this COM method is required for best UX.
   */
  async comReplaceText(params: {
    documentId: string;
    slideId: number;
    shapes: Array<{
      shapeId: number;
      replacements: Array<{
        find: string;
        replace: string;
        matchCase?: boolean;
      }>;
    }>;
  }): Promise<{
    success: boolean;
    results?: Array<{
      shapeId: number;
      replacementsApplied: number;
      warnings: string[];
    }>;
    totalReplacements?: number;
    error?: string;
  }> {
    console.log(`[BackendClient] COM replaceText: ${params.shapes.length} shapes on slide ${params.slideId}`);

    const response = await fetch(`${this.baseUrl}/com/replaceText`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: `COM backend error (${response.status}): ${errorText}`,
      };
    }

    return response.json();
  }

  // REMOVED: comInsertSlide - deprecated, use comAddEmptySlide instead

  /**
   * Delete slides at specified positions via COM backend.
   */
  async comDeleteSlides(params: {
    documentId: string;
    slideIndexes: number[];
  }): Promise<{
    success: boolean;
    deletedCount: number;
    error?: string;
  }> {
    console.log(`[BackendClient] COM deleteSlides: ${params.slideIndexes.length} slides`);

    const response = await fetch(`${this.baseUrl}/com/deleteSlides`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        deletedCount: 0,
        error: `COM backend error (${response.status}): ${errorText}`,
      };
    }

    return response.json();
  }

  /**
   * Reorder slides in the presentation via COM backend.
   */
  async comReorderSlides(params: {
    documentId: string;
    moves: Array<{ fromIndex: number; toIndex: number }>;
  }): Promise<{
    success: boolean;
    error?: string;
  }> {
    console.log(`[BackendClient] COM reorderSlides: ${params.moves.length} moves`);

    const response = await fetch(`${this.baseUrl}/com/reorderSlides`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: `COM backend error (${response.status}): ${errorText}`,
      };
    }

    return response.json();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UNDO CHECKPOINT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create an undo checkpoint in PowerPoint via COM.
   * 
   * Called at the start of agent execution so all operations performed by
   * the agent can be undone as a single unit with Ctrl+Z.
   * 
   * @param documentId - The deck ID to target
   * @param label - Optional label for debugging (e.g., agent name)
   */
  async createUndoCheckpoint(documentId: string, label?: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    console.log(`[BackendClient] Creating undo checkpoint${label ? ` (${label})` : ''}`);

    try {
      const response = await fetch(`${this.baseUrl}/com/undoCheckpoint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentId,
          label,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `COM backend error (${response.status}): ${errorText}`,
        };
      }

      return response.json();
    } catch (error) {
      // Non-fatal - log warning but don't fail the operation
      console.warn(`[BackendClient] Undo checkpoint failed (non-fatal):`, error);
      return {
        success: false,
        error: String(error),
      };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UNDO STEPS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Perform N undo operations in PowerPoint via COM.
   * 
   * @param documentId - The deck ID to target
   * @param count - Number of undo steps to perform (>= 1)
   * @param label - Optional checkpoint label for logging
   */
  async undoSteps(documentId: string, count: number, label?: string): Promise<{
    success: boolean;
    requested?: number;
    performed?: number;
    message?: string;
    errors?: string[];
    error?: string;
  }> {
    console.log(`[BackendClient] Undo steps: ${count}${label ? ` (${label})` : ''}`);

    try {
      const response = await fetch(`${this.baseUrl}/com/undoSteps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentId,
          count,
          label,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `COM backend error (${response.status}): ${errorText}`,
        };
      }

      return response.json();
    } catch (error) {
      console.warn(`[BackendClient] Undo steps failed (non-fatal):`, error);
      return {
        success: false,
        error: String(error),
      };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UNDO MANAGER (HISTORY/TRAVERSAL)
  // ═══════════════════════════════════════════════════════════════════════════

  async getUndoHistory(documentId: string): Promise<{
    success: boolean;
    undoList?: string[];
    redoList?: string[];
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/com/undoManager`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId,
          action: 'list',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: `COM backend error (${response.status}): ${errorText}` };
      }

      return response.json();
    } catch (error) {
      return { success: false, error: String(error) };
    }
  }

  async createUndoCheckpointManaged(documentId: string, label?: string): Promise<{
    success: boolean;
    checkpointId?: string;
    undoList?: string[];
    redoList?: string[];
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/com/undoManager`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId,
          action: 'createCheckpoint',
          label,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: `COM backend error (${response.status}): ${errorText}` };
      }

      return response.json();
    } catch (error) {
      return { success: false, error: String(error) };
    }
  }

  async undoToIndex(documentId: string, undoIndex: number): Promise<{
    success: boolean;
    undoList?: string[];
    redoList?: string[];
    performed?: number;
    targetIndex?: number;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/com/undoManager`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId,
          action: 'undoToIndex',
          undoIndex,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: `COM backend error (${response.status}): ${errorText}` };
      }

      return response.json();
    } catch (error) {
      return { success: false, error: String(error) };
    }
  }

  async redoToIndex(documentId: string, redoIndex: number): Promise<{
    success: boolean;
    undoList?: string[];
    redoList?: string[];
    performed?: number;
    targetIndex?: number;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/com/undoManager`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId,
          action: 'redoToIndex',
          redoIndex,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: `COM backend error (${response.status}): ${errorText}` };
      }

      return response.json();
    } catch (error) {
      return { success: false, error: String(error) };
    }
  }

  async undoToCheckpoint(documentId: string, checkpointId: string, reason?: string): Promise<{
    success: boolean;
    checkpointId?: string;
    undoList?: string[];
    redoList?: string[];
    performed?: number;
    targetIndex?: number;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/com/undoManager`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId,
          action: 'undoToCheckpoint',
          checkpointId,
          reason,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: `COM backend error (${response.status}): ${errorText}` };
      }

      return response.json();
    } catch (error) {
      return { success: false, error: String(error) };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRESENTATION CATALOG ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get DB-backed presentation catalog (source of truth for deck discovery).
   */
  async getPresentationsCatalog(): Promise<PresentationCatalogEntry[]> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    try {
      const response = await fetch(`${this.baseUrl}/presentations/catalog`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Catalog endpoint error (${response.status}): ${errorText}`);
      }

      const payload = await response.json() as {
        success: boolean;
        presentations?: PresentationCatalogEntry[];
        error?: string;
      };

      if (!payload.success) {
        throw new Error(payload.error || 'Catalog request failed');
      }

      return Array.isArray(payload.presentations) ? payload.presentations : [];
    } catch (error) {
      console.error('[BackendClient] getPresentationsCatalog failed:', error);
      throw error;
    }
  }

  /**
   * Upsert presentation entry in DB-backed registry catalog.
   */
  async upsertPresentationCatalogEntry(
    request: PresentationCatalogUpsertRequest
  ): Promise<PresentationCatalogEntry> {
    const authHeader = authService.getAuthHeader();
    if (!authHeader) {
      throw new Error('Not authenticated. Please login first.');
    }

    try {
      const response = await fetch(`${this.baseUrl}/presentations/upsert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Catalog upsert error (${response.status}): ${errorText}`);
      }

      const payload = await response.json() as {
        success: boolean;
        deck_id: string;
        title: string;
        slide_count: number;
        status: PresentationCatalogEntry['status'];
        created_at?: string;
        last_processed?: string;
        error?: string;
      };

      if (!payload.success) {
        throw new Error(payload.error || `Catalog upsert failed for ${request.documentId}`);
      }

      return {
        deck_id: payload.deck_id,
        title: payload.title,
        slide_count: payload.slide_count,
        status: payload.status,
        created_at: payload.created_at,
        last_processed: payload.last_processed,
      };
    } catch (error) {
      console.error('[BackendClient] upsertPresentationCatalogEntry failed:', error);
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRESENTATION OVERVIEW ENDPOINTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get full presentation overview (from completed processing).
   * Returns styles, layout descriptions, slide descriptions from LLM analysis.
   */
  async getPresentationOverview(documentId: string): Promise<{
    success: boolean;
    overview?: {
      total_slides: number;
      slide_descriptions: Array<{
        slide_id?: string;
        slide_index?: number;
        layout_name?: string;
        description: string;
        slide_type: string;
      }>;
      top_style_signatures: Array<{
        hash: string;
        count: number;
        style: Record<string, unknown>;
        usage_percentage: number;
      }>;
      layout_distribution: Record<string, number>;
      slide_type_distribution: Record<string, number>;
    };
    layouts: Array<{
      layout_id: string;
      description: string;
      master_id?: string;
      master_name?: string;
      has_preview_image: boolean;
    }>;
    style_registry?: {
      schema: string;
      deck_id?: string;
      generated_at?: string;
      defsXml: string[];
      usage: Record<string, unknown>;
    } | null;
    error?: string;
  }> {
    console.log(`[BackendClient] Getting presentation overview for deck ${documentId}`);

    try {
      return await this.pythonApiClient.requestJsonPublic<{
        success: boolean;
        overview?: {
          total_slides: number;
          slide_descriptions: Array<{
            slide_id?: string;
            slide_index?: number;
            layout_name?: string;
            description: string;
            slide_type: string;
          }>;
          top_style_signatures: Array<{
            hash: string;
            count: number;
            style: Record<string, unknown>;
            usage_percentage: number;
          }>;
          layout_distribution: Record<string, number>;
          slide_type_distribution: Record<string, number>;
        };
        layouts: Array<{
          layout_id: string;
          description: string;
          master_id?: string;
          master_name?: string;
          has_preview_image: boolean;
        }>;
        style_registry?: {
          schema: string;
          deck_id?: string;
          generated_at?: string;
          defsXml: string[];
          usage: Record<string, unknown>;
        } | null;
        error?: string;
      }>(`/presentations/${documentId}/overview`, { method: "GET" }, { requireAuth: true, retries: 0 });
    } catch (error) {
      console.error(`[BackendClient] Presentation overview fetch error:`, error);
      return { success: false, layouts: [], error: String(error) };
    }
  }

  /**
   * Get quick presentation overview (no LLM, just shape parsing).
   * Used as fallback when full processing hasn't completed.
   */
  async getQuickPresentationOverview(documentId: string, slides: Array<{
    slideIndex: number;
    pptxBase64: string;
    layoutId?: string;
    layoutName?: string;
  }>): Promise<{
    success: boolean;
    status: 'quick' | 'full';
    total_slides: number;
    top_styles: Array<{
      hash: string;
      count: number;
      style: Record<string, unknown>;
      usage_percentage: number;
    }>;
    layouts: Array<{
      layout_id: string;
      layout_name: string;
      count: number;
    }>;
    error?: string;
  }> {
    console.log(`[BackendClient] Getting quick overview for deck ${documentId} (${slides.length} slides)`);

    try {
      return await this.pythonApiClient.requestJsonPublic<{
        success: boolean;
        status: "quick" | "full";
        total_slides: number;
        top_styles: Array<{
          hash: string;
          count: number;
          style: Record<string, unknown>;
          usage_percentage: number;
        }>;
        layouts: Array<{
          layout_id: string;
          layout_name: string;
          count: number;
        }>;
        error?: string;
      }>(
        `/presentations/${documentId}/quick-overview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            slides: slides.map((s) => ({
              slide_index: s.slideIndex,
              pptx_base64: s.pptxBase64,
              layout_id: s.layoutId,
              layout_name: s.layoutName,
            })),
          }),
        },
        { requireAuth: true, retries: 0 }
      );
    } catch (error) {
      console.error(`[BackendClient] Quick overview fetch error:`, error);
      return { 
        success: false, 
        status: 'quick',
        total_slides: slides.length,
        top_styles: [], 
        layouts: [], 
        error: String(error) 
      };
    }
  }

  /**
   * Parse one slide payload and return quick metadata (v2 endpoint).
   * No auth header required to preserve existing slide-capture behavior.
   */
  async parseSlideQuickMetadata(pptxBase64: string): Promise<unknown | undefined> {
    try {
      const data = await this.pythonApiClient.requestJsonPublic<any>(
        "/slides/parse_v2",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            pptx_base64: pptxBase64,
            detail_level: "complete",
            include_notes: false,
          }),
        },
        { retries: 0 }
      );
      return data?.slide?.quick_metadata;
    } catch (error) {
      console.warn("[BackendClient] parseSlideQuickMetadata error:", error);
      return undefined;
    }
  }

  /**
   * Batch parse slide payloads and return raw v2 batch parse response.
   * No auth header required to preserve existing slide-capture behavior.
   */
  async parseSlideQuickMetadataBatch(
    slides: Array<{ slideId: string; pptxBase64: string }>
  ): Promise<unknown | undefined> {
    try {
      return await this.pythonApiClient.requestJsonPublic<unknown>(
        "/slides/parse_batch_v2",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            slides: slides.map((slide) => ({
              slide_id: slide.slideId,
              pptx_base64: slide.pptxBase64,
            })),
            detail_level: "complete",
            extract_metadata_only: true,
            max_concurrent: 10,
          }),
        },
        { retries: 0 }
      );
    } catch (error) {
      console.warn("[BackendClient] parseSlideQuickMetadataBatch error:", error);
      return undefined;
    }
  }

  /**
   * Re-scan one Excel artifact via Excel backend.
   */
  async rescanOneExcelArtifact(payload: {
    source_file_path: string;
    sheet_name: string;
    artifact_type: "chart" | "range";
    source_ref: string;
    last_synced_at?: string;
    export_options?: {
      black_and_white: boolean;
      collapse_groups: boolean;
      clear_comments: boolean;
    };
    force_regenerate?: boolean;
  }): Promise<{
    success: boolean;
    status: "new" | "no_change" | "not_found" | "error";
    source_last_modified: string | null;
    updated_image_base64?: string;
    error?: string;
  }> {
    try {
      return await this.excelApiClient.requestJsonPublic<{
        success: boolean;
        status: "new" | "no_change" | "not_found" | "error";
        source_last_modified: string | null;
        updated_image_base64?: string;
        error?: string;
      }>(
        "/excel/artifact/rescan-one",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        { retries: 0 }
      );
    } catch (error) {
      return {
        success: false,
        status: "error",
        source_last_modified: null,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Get Excel file modified info via Excel backend.
   */
  async getExcelFileModifiedInfo(filePath: string): Promise<{
    exists: boolean;
    last_modified: string | null;
    size_bytes: number | null;
    error?: string;
  }> {
    try {
      return await this.excelApiClient.requestJsonPublic<{
        exists: boolean;
        last_modified: string | null;
        size_bytes: number | null;
        error?: string;
      }>(
        `/excel/file/modified?file_path=${encodeURIComponent(filePath)}`,
        { method: "GET" },
        { retries: 0 }
      );
    } catch (error) {
      return {
        exists: false,
        last_modified: null,
        size_bytes: null,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Regenerate one Excel artifact via Excel backend.
   */
  async regenerateExcelArtifact(payload: {
    source_file_path: string;
    sheet_name: string;
    artifact_type: "chart" | "range";
    source_ref: string;
    export_options?: {
      black_and_white: boolean;
      collapse_groups: boolean;
      clear_comments: boolean;
    };
  }): Promise<{
    success: boolean;
    image_base64?: string;
    chart_data?: Record<string, unknown>;
    artifact_type?: "chart" | "range";
    source_last_modified?: string;
    error?: string;
  }> {
    try {
      return await this.excelApiClient.requestJsonPublic<{
        success: boolean;
        image_base64?: string;
        chart_data?: Record<string, unknown>;
        artifact_type?: "chart" | "range";
        source_last_modified?: string;
        error?: string;
      }>(
        "/excel/artifact/regenerate",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        { retries: 0 }
      );
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }
}

/**
 * Singleton instance for centralized backend communication.
 * Use this instead of creating new BackendClient instances.
 */
export const backendClient = new BackendClient();

